import { useMemo, useState } from 'react';
import { api } from '../lib/api';
import { ErrorBox } from './DataTable';

type Row = Record<string, unknown>;

/**
 * 차량 유종·규격 일괄 입력(게시판 #210 함남열) — 기준정보 › 차량에서 **지금 검색 결과 전체**에 같은 값을 넣는다.
 * 기본은 빈 칸인 차량만(이미 적힌 값은 「이미 적힌 차량도 바꾸기」를 켰을 때만). 서버 `setCarTypeGubunBulk`.
 */
export default function CarTypeBulkForm({
  rows,
  onClose,
  onSaved,
}: {
  rows: Row[];
  onClose: () => void;
  onSaved: (msg: string) => void;
}) {
  const [value, setValue] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const empty = (r: Row) => String(r.car_type_gubun ?? '').trim() === '';
  const known = useMemo(
    () => [...new Set(rows.map((r) => String(r.car_type_gubun ?? '').trim()).filter(Boolean))].sort(),
    [rows],
  );
  const targets = rows.filter((r) => overwrite || empty(r)).filter((r) => String(r.car_type_gubun ?? '').trim() !== value.trim());

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const r = await api.setCarTypeGubunBulk({
        carIds: targets.map((t) => Number(t.car_id)),
        value: value.trim(),
        overwrite,
      });
      onSaved(`유종·규격 「${value.trim() || '(비움)'}」 — ${r.changed}대 넣었습니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex items-center gap-3">
        <h2 className="text-base font-bold">유종·규격 일괄 입력</h2>
        <span className="text-xs text-ink-faint">지금 검색 결과 {rows.length.toLocaleString()}대</span>
        <button className="btn-ghost ml-auto" onClick={onClose} disabled={saving}>
          취소
        </button>
        <button className="btn-primary" onClick={save} disabled={saving || targets.length === 0 || (!value.trim() && !overwrite)}>
          {saving ? '넣는 중…' : `${targets.length.toLocaleString()}대에 넣기`}
        </button>
      </div>

      {error && <ErrorBox message={error} />}

      <div className="flex flex-wrap items-end gap-4">
        <label className="block text-sm">
          <span className="mb-1 block text-xs text-ink-soft">넣을 유종·규격</span>
          <input
            className="field w-56"
            list="car-type-gubun-known"
            placeholder="예: 디젤대형"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoFocus
          />
          <datalist id="car-type-gubun-known">
            {known.map((k) => <option key={k} value={k} />)}
          </datalist>
        </label>
        <label className="flex cursor-pointer select-none items-center gap-2 text-sm">
          <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
          이미 적힌 차량도 바꾸기
        </label>
      </div>

      <p className="text-xs text-ink-faint">
        먼저 위 검색·칸별 검색으로 바꿀 차량만 남긴 뒤 여기서 넣으세요. 기본은 유종·규격이 빈 차량에만 넣고, 바뀐 내용은 차량마다 변경기록에 남습니다.
      </p>

      <div className="max-h-[40vh] overflow-auto rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-canvas text-xs text-ink-soft">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">차량번호</th>
              <th className="px-2 py-1.5 text-left font-medium">차량명</th>
              <th className="px-2 py-1.5 text-left font-medium">지금 유종·규격</th>
              <th className="px-2 py-1.5 text-left font-medium">바뀜</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 300).map((r) => {
              const hit = targets.includes(r);
              return (
                <tr key={String(r.car_id)} className={`border-t border-line ${hit ? '' : 'text-ink-faint'}`}>
                  <td className="px-2 py-1">{String(r.car_no ?? '')}</td>
                  <td className="px-2 py-1">{String(r.car_name ?? '')}</td>
                  <td className="px-2 py-1">{empty(r) ? '—' : String(r.car_type_gubun)}</td>
                  <td className="px-2 py-1">{hit ? `→ ${value.trim() || '(비움)'}` : '그대로'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {rows.length > 300 && (
          <p className="px-2 py-1.5 text-xs text-ink-faint">앞 300대만 보여 줍니다(넣기는 {targets.length.toLocaleString()}대 전부).</p>
        )}
      </div>
    </div>
  );
}
